import React from 'react';
import {
  Badge, Button, Col, Form, Row,
} from 'react-bootstrap';
import { SELECTABLE_DOC_TYPES, docTypeLabel } from '../../../Utils/billingoDocuments';

// The statuses the UI derives and displays. The API maps each onto the stored
// Billingo values that mean the same thing.
const STATUSES = [
  { value: 'paid', label: 'Paid' },
  { value: 'partially_paid', label: 'Partially paid' },
  { value: 'awaiting_payment', label: 'Awaiting payment' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const EMPTY_FILTERS = {
  q: '',
  type: '',
  status: '',
  created_by: '',
  currency: '',
  from: '',
  to: '',
  min_total: '',
  max_total: '',
  // Uploaded PDFs (backfilled legacy invoices) are left out unless asked for:
  // they are records of invoices issued elsewhere, not Billingo documents.
  uploads: false,
};

// Search is shown on its own; an unset field is '', null or an off switch.
export const countActiveFilters = (filters) => Object.entries(filters)
  .filter(([key, value]) => key !== 'q' && value !== '' && value != null && value !== false)
  .length;

/**
 * Filter bar for the invoices list. Every control maps to a server-side filter,
 * so the result is the whole matching set paged — not just whatever happened to
 * be on the page in the browser.
 */
function InvoiceFilters({
  filters, onChange, facets = { created_by: [], currencies: [] }, open, onToggle,
}) {
  const set = (field) => (e) => onChange({ ...filters, [field]: e.target.value });
  const active = countActiveFilters(filters);

  return (
    <div className="mb-3">
      <Row className="g-2 align-items-center">
        <Col xs={12} md={5}>
          <Form.Control
            size="sm"
            placeholder="Search number, order reference or line…"
            value={filters.q}
            onChange={set('q')}
          />
        </Col>
        <Col xs={6} md={3}>
          <Form.Select size="sm" value={filters.type} onChange={set('type')}>
            <option value="">All types</option>
            {SELECTABLE_DOC_TYPES.map((t) => (
              <option key={t} value={t}>{docTypeLabel(t)}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={6} md={2}>
          <Form.Select size="sm" value={filters.status} onChange={set('status')}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Form.Select>
        </Col>
        <Col xs={12} md={2} className="d-flex gap-2">
          <Button size="sm" variant={open ? 'secondary' : 'outline-secondary'} onClick={onToggle} className="flex-grow-1">
            <i className="fa fa-sliders me-1" />
            More
            {active > 0 && <Badge bg="primary" className="ms-1">{active}</Badge>}
          </Button>
        </Col>
      </Row>

      {open && (
        <Row className="g-2 mt-1 p-2 border rounded bg-light-subtle">
          <Form.Group as={Col} xs={6} md={3} controlId="invoice-filter-from">
            <Form.Label className="small text-muted mb-1">Issued from</Form.Label>
            <Form.Control size="sm" type="date" value={filters.from} onChange={set('from')} />
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} controlId="invoice-filter-to">
            <Form.Label className="small text-muted mb-1">Issued to</Form.Label>
            <Form.Control size="sm" type="date" value={filters.to} onChange={set('to')} />
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} controlId="invoice-filter-created_by">
            <Form.Label className="small text-muted mb-1">Issued by</Form.Label>
            <Form.Select size="sm" value={filters.created_by} onChange={set('created_by')}>
              <option value="">Anyone</option>
              {(facets.created_by || []).map((u) => <option key={u} value={u}>{u}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} controlId="invoice-filter-currency">
            <Form.Label className="small text-muted mb-1">Currency</Form.Label>
            <Form.Select size="sm" value={filters.currency} onChange={set('currency')}>
              <option value="">Any</option>
              {(facets.currencies || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} controlId="invoice-filter-min_total">
            <Form.Label className="small text-muted mb-1">Min total</Form.Label>
            <Form.Control size="sm" type="number" value={filters.min_total} onChange={set('min_total')} />
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} controlId="invoice-filter-max_total">
            <Form.Label className="small text-muted mb-1">Max total</Form.Label>
            <Form.Control size="sm" type="number" value={filters.max_total} onChange={set('max_total')} />
          </Form.Group>
          <Col xs={12} md={6} className="d-flex align-items-end justify-content-between gap-2">
            <Form.Check
              type="switch"
              id="invoice-filter-uploads"
              label="Include uploaded PDFs"
              className="small mb-1"
              checked={filters.uploads}
              onChange={(e) => onChange({ ...filters, uploads: e.target.checked })}
            />
            <Button
              size="sm"
              variant="link"
              className="text-muted"
              disabled={active === 0 && !filters.q}
              onClick={() => onChange({ ...EMPTY_FILTERS })}
            >
              Clear all filters
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default InvoiceFilters;
